import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { api } from '../../services/api';
import PasswordInput from '../../components/PasswordInput';

export default function ChangePasswordForm() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const minLengthOk = newPassword.length >= 8;
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = !!currentPassword && minLengthOk && passwordsMatch && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !token) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.changePassword({ currentPassword, newPassword }, token);
      setSuccess(t('settings.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.failedToChangePassword'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-section">
      <h3 className="settings-section-title">{t('settings.changePassword')}</h3>
      {success && <div className="settings-toast settings-toast-success">{success}</div>}
      {error && <div className="settings-toast settings-toast-error">{error}</div>}
      <form onSubmit={handleSubmit} className="settings-form">
        <div className="settings-form-row">
          <label htmlFor="change-password-current">{t('settings.currentPassword')}*</label>
          <PasswordInput
            id="change-password-current"
            value={currentPassword}
            onChange={(value) => { setCurrentPassword(value); setError(''); }}
            placeholder={t('settings.currentPassword')}
            autoComplete="current-password"
          />
        </div>
        <div className="settings-form-row">
          <label htmlFor="change-password-new">{t('settings.newPassword')}*</label>
          <PasswordInput
            id="change-password-new"
            value={newPassword}
            onChange={(value) => { setNewPassword(value); setError(''); }}
            placeholder={t('settings.newPassword')}
            autoComplete="new-password"
            minLength={8}
          />
          {newPassword && !minLengthOk && (
            <span className="field-error">{t('settings.minChars')}</span>
          )}
        </div>
        <div className="settings-form-row">
          <label htmlFor="change-password-confirm">{t('settings.confirmPassword')}*</label>
          <PasswordInput
            id="change-password-confirm"
            value={confirmPassword}
            onChange={(value) => { setConfirmPassword(value); setError(''); }}
            placeholder={t('settings.confirmPassword')}
            autoComplete="new-password"
          />
          {confirmPassword && !passwordsMatch && (
            <span className="field-error">{t('settings.passwordsMismatch')}</span>
          )}
        </div>
        <button type="submit" className="settings-btn-primary" disabled={!canSubmit}>
          {loading ? t('common.saving') : t('settings.changePassword')}
        </button>
      </form>
    </div>
  );
}
